import React from 'react';
import { Box, Card, Skeleton, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import { TEST_MODE_COPY } from '../testMode.copy';
import { TestModeKpiCounts, TestModeKpiKey } from '../testMode.types';

interface TileSpec {
    key: TestModeKpiKey;
    label: string;
    caption: string;
    accent: (theme: Theme) => string;
    number: (theme: Theme) => string;
}

const TILES: TileSpec[] = [
    {
        key: 'inTestMode',
        label: TEST_MODE_COPY.kpi.inTestMode.label,
        caption: TEST_MODE_COPY.kpi.inTestMode.caption,
        accent: (t) => t.palette.primary.main,
        number: (t) => (t.palette.primary as any).darker ?? t.palette.primary.dark,
    },
    {
        key: 'completed',
        label: TEST_MODE_COPY.kpi.completed.label,
        caption: TEST_MODE_COPY.kpi.completed.caption,
        accent: (t) => t.palette.success.main,
        number: (t) => t.palette.success.dark,
    },
    {
        key: 'notTested',
        label: TEST_MODE_COPY.kpi.notTested.label,
        caption: TEST_MODE_COPY.kpi.notTested.caption,
        accent: (t) => t.palette.warning.main,
        number: (t) => t.palette.warning.dark,
    },
    {
        key: 'flagged',
        label: TEST_MODE_COPY.kpi.flagged.label,
        caption: TEST_MODE_COPY.kpi.flagged.caption,
        accent: (t) => t.palette.error.main,
        number: (t) => t.palette.error.dark,
    },
];

interface Props {
    counts: TestModeKpiCounts;
    loading: boolean;
    active: TestModeKpiKey | null;
    onToggle: (key: TestModeKpiKey) => void;
}

/**
 * Zone 1 of the Test Mode tab: four clickable KPI tiles that constrain the
 * status table to a cohort. Clicking an active tile (or Clear) deselects.
 */
export const KpiTiles: React.FC<Props> = ({ counts, loading, active, onToggle }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: '18px',
                mb: 3,
            }}
        >
            {TILES.map((tile) => {
                const accent = tile.accent(theme);
                const value = counts[tile.key];
                const unavailable = value === null || value === undefined;
                const clickable = tile.key !== 'flagged' || !unavailable;
                const selected = active === tile.key;

                const card = (
                    <Card
                        key={tile.key}
                        onClick={clickable ? () => onToggle(tile.key) : undefined}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        onKeyDown={
                            clickable
                                ? (e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          onToggle(tile.key);
                                      }
                                  }
                                : undefined
                        }
                        sx={{
                            p: '18px 20px',
                            borderRadius: '14px',
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                            cursor: clickable ? 'pointer' : 'default',
                            backgroundColor: selected ? alpha(accent, 0.05) : 'background.paper',
                            boxShadow: selected
                                ? `0 0 0 2px ${accent}, 0 0 0 5px ${alpha(accent, 0.24)}`
                                : undefined,
                            transition: theme.transitions.create(['box-shadow', 'background-color'], {
                                duration: theme.transitions.duration.shorter,
                            }),
                            '&:hover': clickable
                                ? { boxShadow: selected ? undefined : theme.shadows[8] }
                                : undefined,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    component="span"
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: accent,
                                        boxShadow: `0 0 0 3px ${alpha(accent, 0.2)}`,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: '0.6px',
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {tile.label}
                                </Typography>
                            </Box>
                            {selected && (
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: tile.number(theme),
                                        backgroundColor: alpha(accent, 0.14),
                                        px: '7px',
                                        py: '2px',
                                        borderRadius: '20px',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {TEST_MODE_COPY.kpi.filteringChip}
                                </Typography>
                            )}
                        </Box>
                        {loading ? (
                            <Skeleton width={56} height={42} sx={{ mt: '10px' }} />
                        ) : (
                            <Typography
                                sx={{
                                    fontSize: 34,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    mt: '10px',
                                    color: unavailable ? 'text.disabled' : tile.number(theme),
                                }}
                            >
                                {unavailable ? '—' : value}
                            </Typography>
                        )}
                        <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: '6px' }}>
                            {tile.caption}
                        </Typography>
                    </Card>
                );

                return tile.key === 'flagged' && unavailable ? (
                    <Tooltip key={tile.key} title={TEST_MODE_COPY.kpi.flaggedUnavailable} arrow>
                        {card}
                    </Tooltip>
                ) : (
                    card
                );
            })}
        </Box>
    );
};
